import { FormEvent, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  Adresse,
  AdresseInput,
  createAdresse,
  deleteAdresse,
  fetchAdressen,
  geocodeAdresse,
} from "@/lib/api"
import { useToast } from "@/components/Toast"
import { SkeletonZeile } from "@/components/Skeleton"
import { Badge, Button, Input } from "@/components/ui"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"

const ANSCHRIFTTYPEN: [string, string][] = [
  ["hauptanschrift", "Hauptanschrift"],
  ["lieferanschrift", "Lieferanschrift"],
  ["postanschrift", "Postanschrift"],
  ["rechnungsanschrift", "Rechnungsanschrift"],
  ["bestellanschrift", "Bestellanschrift"],
  ["briefanschrift", "Briefanschrift"],
]

const LEER: AdresseInput = {
  strasse: "",
  plz: "",
  ort: "",
  anschrifttypen: [],
  istStandort: false,
  standortbezeichnung: "",
}

/** Adressverwaltung innerhalb der Organisation-Detailseite — AP-2.3. */
export function AdressenSection({ organisationId }: { organisationId: string }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [neu, setNeu] = useState<AdresseInput>(LEER)
  const [zeigeNeu, setZeigeNeu] = useState(false)
  const [loeschenId, setLoeschenId] = useState<string | null>(null)

  const { data: adressen, isLoading } = useQuery({
    queryKey: ["adressen", organisationId],
    queryFn: () => fetchAdressen(organisationId),
  })

  const invalidieren = () =>
    queryClient.invalidateQueries({ queryKey: ["adressen", organisationId] })

  const anlegen = useMutation({
    mutationFn: (payload: AdresseInput) => createAdresse({ ...payload, organisationId }),
    onSuccess: () => {
      invalidieren()
      setNeu(LEER)
      setZeigeNeu(false)
      toast.show("Adresse gespeichert.", "success")
    },
    onError: () => toast.show("Adresse konnte nicht gespeichert werden.", "error"),
  })

  const loeschen = useMutation({
    mutationFn: (id: string) => deleteAdresse(id),
    onSuccess: () => {
      invalidieren()
      setLoeschenId(null)
      toast.show("Adresse gelöscht.", "success")
    },
    onError: () => toast.show("Adresse konnte nicht gelöscht werden.", "error"),
  })

  const geocodieren = useMutation({
    mutationFn: (adresse: Adresse) =>
      geocodeAdresse(
        adresse.id,
        [adresse.strasse, adresse.plz, adresse.ort].filter(Boolean).join(", "),
      ),
    onSuccess: () => {
      invalidieren()
      toast.show("Adresse geocodiert.", "success")
    },
    onError: () => toast.show("Geocodierung fehlgeschlagen.", "error"),
  })

  function onSubmitNeu(e: FormEvent) {
    e.preventDefault()
    anlegen.mutate(neu)
  }

  function toggleTyp(t: string) {
    setNeu((f) => ({
      ...f,
      anschrifttypen: f.anschrifttypen?.includes(t)
        ? f.anschrifttypen.filter((x) => x !== t)
        : [...(f.anschrifttypen ?? []), t],
    }))
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Adressen &amp; Standorte</h2>
        <button
          type="button"
          onClick={() => setZeigeNeu(true)}
          className="rounded-md border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          + Adresse hinzufügen
        </button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <SkeletonZeile className="h-16 w-full" />
          <SkeletonZeile className="h-16 w-full" />
        </div>
      )}

      <ul className="space-y-3">
        {adressen?.map((a) => (
          <li key={a.id} className="rounded-md border border-slate-100 p-3 text-sm">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-medium text-slate-900">
                  {a.strasse}, {a.plz} {a.ort}
                  {a.istStandort && (
                    <Badge tone="slate" className="ml-2">
                      Standort{a.standortbezeichnung ? `: ${a.standortbezeichnung}` : ""}
                    </Badge>
                  )}
                </p>
                <p className="text-slate-500">
                  {a.anschrifttypen
                    .map((t) => ANSCHRIFTTYPEN.find(([v]) => v === t)?.[1] ?? t)
                    .join(", ") || "—"}
                </p>
                {a.geoLat != null && a.geoLon != null ? (
                  <p className="mt-1 text-xs text-green-700">
                    Geocodiert: {a.geoLat.toFixed(5)}, {a.geoLon.toFixed(5)}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-slate-400">Noch nicht geocodiert</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => geocodieren.mutate(a)}
                  disabled={geocodieren.isPending}
                  className="rounded-md border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  Geocodieren
                </button>
                {loeschenId === a.id ? (
                  <span className="flex items-center gap-1.5 text-xs">
                    <span className="text-slate-600">Wirklich löschen?</span>
                    <button
                      type="button"
                      onClick={() => loeschen.mutate(a.id)}
                      disabled={loeschen.isPending}
                      className="rounded-md bg-red-600 px-2 py-1 text-white hover:bg-red-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                    >
                      Ja, löschen
                    </button>
                    <button
                      type="button"
                      onClick={() => setLoeschenId(null)}
                      className="rounded-md border border-slate-300 px-2 py-1 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                    >
                      Abbrechen
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setLoeschenId(a.id)}
                    className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                  >
                    Löschen
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
        {adressen?.length === 0 && !zeigeNeu && (
          <p className="text-sm text-slate-400">Noch keine Adressen erfasst.</p>
        )}
      </ul>

      <Sheet open={zeigeNeu} onOpenChange={setZeigeNeu}>
        <SheetContent sheetKey="adresse-anlegen" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Neue Adresse</SheetTitle>
            <SheetDescription>Adresse oder Standort für diese Organisation erfassen.</SheetDescription>
          </SheetHeader>
          <form id="adresse-neu-form" onSubmit={onSubmitNeu} className="grid gap-4 py-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-3 space-y-1.5 sm:col-span-1">
                <label className="block text-xs font-medium text-slate-700">Straße</label>
                <Input
                  value={neu.strasse ?? ""}
                  onChange={(e) => setNeu({ ...neu, strasse: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">PLZ</label>
                <Input
                  value={neu.plz ?? ""}
                  onChange={(e) => setNeu({ ...neu, plz: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">Ort</label>
                <Input
                  value={neu.ort ?? ""}
                  onChange={(e) => setNeu({ ...neu, ort: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <span className="block text-xs font-medium text-slate-700">Anschrifttypen</span>
              <div className="flex flex-wrap gap-3 text-sm text-slate-700">
                {ANSCHRIFTTYPEN.map(([value, label]) => (
                  <label key={value} className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={neu.anschrifttypen?.includes(value) ?? false}
                      onChange={() => toggleTyp(value)}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={neu.istStandort ?? false}
                onChange={(e) => setNeu({ ...neu, istStandort: e.target.checked })}
              />
              Ist Standort (Ninox GB)
            </label>
            {neu.istStandort && (
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">Standortbezeichnung</label>
                <Input
                  value={neu.standortbezeichnung ?? ""}
                  onChange={(e) => setNeu({ ...neu, standortbezeichnung: e.target.value })}
                />
              </div>
            )}
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => setZeigeNeu(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="adresse-neu-form" disabled={anlegen.isPending}>
              {anlegen.isPending ? "Speichert…" : "Adresse speichern"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  )
}
